import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Container, Row, Col, Spinner, Card, Button as BootstrapButton, Badge, Alert, Modal } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '../CustomButton/Button';
import axiosInstance from '../../utils/axiosInstanse';
import host from "../../host";
import './pool.css';
import { showNotification } from '../../utils/notificationUtils';
import {
    format,
    addMonths,
    startOfMonth,
    endOfMonth,
    eachDayOfInterval,
    isSameMonth,
    isToday,
    isPast,
    isSameDay,
    subMonths,
    startOfWeek,
    endOfWeek,
    getDay,
    isWithinInterval,
    parseISO
} from 'date-fns';
import { ru } from 'date-fns/locale';
import useIsMobile from '../../utils/useIsMobile';
import TIME_SLOTS_MAP from './time_slots'
import { useNavigate } from 'react-router-dom';

// Конвертация дня недели из date-fns (0-6, воскресенье=0) в FDAYW (1-7, понедельник=1)
const convertWeekdayToFDAYW = (dateFnsDay) => {
    if (dateFnsDay === 0) return 7;
    return dateFnsDay;
};

const TimeSlotCard = React.memo(({
    slot,
    stime,
    availableCount,
    totalCount,
    isSelected,
    isMobileView,
    onSelect,
    isBooking,
    bookedSlots,
    selectedWeekday,
    isAvailable
}) => {
    const isBookedByUser = bookedSlots.has(stime);

    const getAvailabilityColor = () => {
        if (availableCount === 0) return '#dc3545';
        if (availableCount <= 2) return '#ffc107';
        if (availableCount <= 4) return '#17a2b8';
        return '#28a745';
    };

    // const getWeekdayName = (weekday) => {
    //     const weekdays = ['воскресенья', 'понедельника', 'вторника', 'среды', 'четверга', 'пятницы', 'субботы'];
    //     return weekdays[weekday] || 'дня';
    // };

    return (
        <motion.div
            layout
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{
                opacity: 1,
                scale: 1,
                transition: {
                    opacity: { duration: 0.2 },
                    scale: { duration: 0.3 }
                }
            }}
            exit={{ opacity: 0, scale: 0.9 }}
            whileHover={isAvailable && !isSelected ? { scale: 1.03 } : {}}
            whileTap={isAvailable && !isSelected ? { scale: 0.98 } : {}}
            className={isMobileView ? "mobile-time-slot-wrapper" : "desktop-time-slot-wrapper"}
        >
            <Card
                className={`${isMobileView ? 'mobile-time-slot-card' : 'desktop-time-slot-card'} 
                    ${isSelected ? 'selected' : ''}
                    ${isBookedByUser ? 'booked-by-user' : ''}
                    ${!isAvailable && !isBookedByUser ? 'fully-booked' : ''}
                    ${isBooking ? 'booking-in-progress' : ''}`}
                style={{
                    borderLeft: `4px solid ${getAvailabilityColor()}`,
                    opacity: isAvailable || isBookedByUser ? 1 : 0.7
                }}
            >
                <Card.Body className="text-center p-1">
                    <h6 className="mb-1">{slot}</h6>

                    <div className="availability-indicator mb-2">
                        <div className="d-flex justify-content-between align-items-center">
                            <small className="text-muted">
                                {isBookedByUser ? (
                                    <span className="text-success">✓ Ваш абонемент</span>
                                ) : (
                                    <>
                                        <span style={{ fontWeight: 'bold', color: getAvailabilityColor() }}>
                                            {availableCount}
                                        </span>
                                        <span className="text-muted"> / {totalCount} мест свободно</span>
                                    </>
                                )}
                            </small>
                        </div>
                        {!isBookedByUser && totalCount > 0 && (
                            <div className="progress" style={{ height: '4px', marginTop: '2px' }}>
                                <div
                                    className="progress-bar"
                                    role="progressbar"
                                    style={{
                                        width: `${100 - (availableCount / totalCount * 100)}%`,
                                        backgroundColor: getAvailabilityColor(),
                                        transition: 'width 0.3s ease'
                                    }}
                                    aria-valuenow={totalCount - availableCount}
                                    aria-valuemin="0"
                                    aria-valuemax={totalCount}
                                />
                            </div>
                        )}
                    </div>

                    {isBookedByUser ? (
                        <Badge bg="success" className="w-100">
                            <i className="bi bi-check-circle"></i> Абонемент оформлен
                        </Badge>
                    ) : (
                        <Button
                            onClick={() => onSelect(slot, stime)}
                            disabled={!isAvailable || isSelected || isBooking}
                            className="booking-button"
                            style={{
                                width: isMobileView ? '100%' : undefined,
                                padding: isMobileView ? '4px 8px' : undefined,
                                fontSize: isMobileView ? '0.75rem' : undefined,
                                opacity: isBooking ? 0.8 : 1,
                            }}
                            variant={isSelected ? "success" : "primary"}
                        >
                            {isBooking ? (
                                <>
                                    <Spinner
                                        animation="border"
                                        size="sm"
                                        className="booking-spinner"
                                    />
                                    {!isMobileView && <span>Оформляем...</span>}
                                </>
                            ) : isSelected ? (
                                <>
                                    <i className="bi bi-check-circle"></i>
                                    {!isMobileView && <span>Выбрано</span>}
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-calendar-plus"></i>
                                    {!isMobileView && <span>Выбрать</span>}
                                    {isMobileView && <span>Выбрать</span>}
                                </>
                            )}
                        </Button>
                    )}
                </Card.Body>
            </Card>
        </motion.div>
    );
});

const BookingConfirmationModal = ({ show, onClose, onConfirm, bookingDetails }) => {
    if (!bookingDetails) return null;

    const { weekday, timeSlot, dates, stime, fdayw } = bookingDetails;

    return (
        <Modal show={show} onHide={onClose} centered size="lg">
            <Modal.Header closeButton className="bg-primary text-white">
                <Modal.Title>Подтверждение бронирования</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="p-3">
                    <h5 className="mb-3">Детали абонемента:</h5>

                    <div className="mb-3 p-3 bg-light rounded">
                        <p><strong>День недели:</strong> {weekday}</p>
                        <p><strong>Время:</strong> {timeSlot}</p>
                        <p><strong>Количество занятий:</strong> {dates?.length > 4 ? "4" : dates?.length}</p>
                    </div>

                    <h6 className="mb-2">Даты занятий:</h6>
                    <div className="dates-list mb-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        {dates && dates.length > 0 ? (
                            dates.map((date, index) => (
                                <div key={index} className="p-2 border-bottom">
                                    {format(date, 'dd.MM.yyyy')}
                                </div>
                            ))
                        ) : (
                            <p className="text-muted">Нет доступных дат</p>
                        )}
                    </div>

                    <Alert variant="info">
                        <i className="bi bi-info-circle"></i> После подтверждения абонемент будет оформлен на выбранные даты.
                    </Alert>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="success"
                    onClick={() => onConfirm({ fdayw, stime })}
                >
                    <i className="bi bi-check-circle"></i> Подтвердить
                </Button>
                <Button variant="secondary" onClick={onClose}>
                    Отмена
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

const getWeekdayName = (weekday) => {
    const weekdays = ['воскресенье', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота'];
    return weekdays[weekday] || 'дня';
};

function PoolBooking() {
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedWeekday, setSelectedWeekday] = useState(null);
    const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
    const [selectedStime, setSelectedStime] = useState(null);
    // Фиксируем следующий месяц при первом рендере
    const [targetMonth] = useState(addMonths(new Date(), 1)); // Изменено: убрал setCurrentMonth
    const [displayMonth, setDisplayMonth] = useState(addMonths(new Date(), 1)); // Добавлено: для отображения
    const [isTimeSlotsVisible, setIsTimeSlotsVisible] = useState(false);
    const [containerHeight, setContainerHeight] = useState('auto');
    const [scheduleData, setScheduleData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [bookedSlots, setBookedSlots] = useState(new Set());
    const [isBookingAvailable, setIsBookingAvailable] = useState(false);
    const navigate = useNavigate();

    const [showModal, setShowModal] = useState(false);
    const [bookingDetails, setBookingDetails] = useState(null);

    const [bookingState, setBookingState] = useState({
        isBooking: false,
        bookingSlot: null
    });

    const dispatch = useDispatch();
    const isMobile = useIsMobile(768);

    const slotsContainerRef = useRef(null);
    const bookingTimerRef = useRef(null);
    const slotsLoadingTimerRef = useRef(null);

    const checkBookingAvailability = useCallback(() => {
        if (!scheduleData) return false;

        const today = new Date();
        const bookStart = parseISO(scheduleData.BOOKB);
        const bookEnd = parseISO(scheduleData.BOOKE);

        const nextDayBookEnd = new Date(bookEnd);
        nextDayBookEnd.setDate(bookEnd.getDate() + 1);

        return isWithinInterval(today, { start: bookStart, end: nextDayBookEnd });
    }, [scheduleData]);

    // Загрузка данных расписания - теперь без зависимости от displayMonth
    useEffect(() => {
        const fetchSchedule = async () => {
            try {
                setLoading(true);
                setError(null);

                const response = await axiosInstance.get(`${host}api/get_pool_schedule/`);

                if (response.data && response.data.BOOKING) {
                    setScheduleData(response.data);
                    setIsBookingAvailable(checkBookingAvailability());
                }
            } catch (err) {
                setError('Не удалось загрузить расписание. Пожалуйста, попробуйте позже.');
                showNotification(dispatch, 'Ошибка загрузки расписания', 'error');
            } finally {
                setLoading(false);
            }
        };

        fetchSchedule();
    }, [dispatch]); // Убрана зависимость от currentMonth/displayMonth

    useEffect(() => {
        if (scheduleData) {
            setIsBookingAvailable(checkBookingAvailability());
        }
    }, [scheduleData, checkBookingAvailability]);

    useEffect(() => {
        if (selectedWeekday !== null && selectedDate) {
            setIsTimeSlotsVisible(false);

            if (slotsLoadingTimerRef.current) {
                clearTimeout(slotsLoadingTimerRef.current);
            }

            slotsLoadingTimerRef.current = setTimeout(() => {
                setIsTimeSlotsVisible(true);
            }, 300);
        }
    }, [selectedWeekday, selectedDate]);

    useEffect(() => {
        if (slotsContainerRef.current && isMobile) {
            const height = slotsContainerRef.current.offsetHeight;
            if (height > 0) {
                setContainerHeight(`${height}px`);
            }
        }
    }, [isMobile, scheduleData, bookingState.isBooking, isTimeSlotsVisible]);

    const getCalendarDays = useCallback(() => {
        const monthStart = startOfMonth(displayMonth); // Используем displayMonth для отображения
        const monthEnd = endOfMonth(displayMonth);
        const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
        const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
        return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
    }, [displayMonth]);

    const getSameWeekdayDates = useCallback((date) => {
        if (!date) return [];

        const targetWeekday = getDay(date);
        const monthStart = startOfMonth(targetMonth); // Используем целевой месяц для расчета дат
        const monthEnd = endOfMonth(targetMonth);
        const allDays = eachDayOfInterval({ start: monthStart, end: monthEnd });

        return allDays.filter(day =>
            getDay(day) === targetWeekday && !isPast(day)
        );
    }, [targetMonth]);

    const getAvailableSlotsForWeekday = useCallback((weekday) => {
        if (!scheduleData || !scheduleData.BOOKING) return {};

        const fdayw = convertWeekdayToFDAYW(weekday);
        const slotsForWeekday = scheduleData.BOOKING.filter(item => item.FDAYW === fdayw);

        const availability = {};
        slotsForWeekday.forEach(item => {
            const timeSlot = TIME_SLOTS_MAP[item.STIME];
            if (timeSlot) {
                availability[timeSlot] = {
                    available: item.TOTAL - item.BOOKD,
                    total: item.TOTAL,
                    stime: item.STIME,
                    booked: item.BOOKD,
                    fdayw: item.FDAYW
                };
            }
        });

        return availability;
    }, [scheduleData]);

    const isDateAvailable = useCallback((date) => {
        if (!scheduleData || !isBookingAvailable) return false;

        const weekday = getDay(date);
        const fdayw = convertWeekdayToFDAYW(weekday);

        const hasSlots = scheduleData.BOOKING.some(item =>
            item.FDAYW === fdayw && (item.TOTAL - item.BOOKD) > 0
        );

        return hasSlots;
    }, [scheduleData, isBookingAvailable]);

    const handleDateSelect = (day) => {
        // Проверяем, что выбранная дата принадлежит целевому месяцу
        if (isSameMonth(day, targetMonth) && !isPast(day)) {
            if (!isDateAvailable(day)) {
                showNotification(dispatch, 'На выбранный день нет доступных абонементов', 'warning');
                return;
            }

            setIsTimeSlotsVisible(false);

            if (slotsLoadingTimerRef.current) {
                clearTimeout(slotsLoadingTimerRef.current);
            }

            setSelectedDate(day);
            setSelectedWeekday(getDay(day));
            setSelectedTimeSlot(null);
            setSelectedStime(null);

            slotsLoadingTimerRef.current = setTimeout(() => {
                setIsTimeSlotsVisible(true);
            }, 300);
        }
    };

    const handleSelectSlot = useCallback(async (slot, stime) => {
        if (bookingState.isBooking) return;

        setSelectedTimeSlot(slot);
        setSelectedStime(stime);

        if (!isBookingAvailable) {
            showNotification(dispatch, 'Бронирование временно недоступно', 'error');
            return;
        }

        const weekdayName = getWeekdayName(selectedWeekday);
        const dates = getSameWeekdayDates(selectedDate);
        const fdayw = convertWeekdayToFDAYW(selectedWeekday);

        setBookingDetails({
            weekday: weekdayName,
            timeSlot: slot,
            stime: stime,
            fdayw: fdayw,
            dates: dates
        });
        setShowModal(true);
    }, [selectedWeekday, selectedDate, isBookingAvailable, bookingState.isBooking, dispatch, getSameWeekdayDates]);

    const handleConfirmBooking = async (bookingData) => {
        setShowModal(false);

        setBookingState({
            isBooking: true,
            bookingSlot: selectedTimeSlot
        });

        try {
            const response = await axiosInstance.post(
                `${host}api/book_pool/`,
                bookingData,
            );
            if (response.data && response.data.SUCCESS) {
                setSelectedTimeSlot(null);
                setSelectedStime(null);
                navigate('/note/pool_active')
            } else {
                throw new Error(response.data?.error || 'Ошибка при бронировании');
            }
        } catch (error) {

            if (error.response) {
                if (error.response.status === 400) {
                    const errorMessage = error.response.data?.ERROR || 'Ошибка при бронировании';
                    showNotification(dispatch, errorMessage, 'error');
                }
                if (error.response.status === 423) {
                    const errorMessage = 'Транзакция заблокирована администратором, попробуйте позже';
                    showNotification(dispatch, errorMessage, 'error');
                }
            } else {
                let errorMessage = 'Ошибка при оформлении абонемента';
                if (error.response) {
                    errorMessage = error.response.data?.error || errorMessage;
                }
                showNotification(dispatch, errorMessage, 'error');
            }
        } finally {
            setBookingState({
                isBooking: false,
                bookingSlot: null
            });
        }
    };

    // Навигация по месяцам - только визуальная
    const handlePrevMonth = () => {
        const prevMonth = subMonths(displayMonth, 1);
        const now = new Date();
        const currentMonthStart = startOfMonth(now);
        // Можно смотреть предыдущие месяцы, но нельзя выбрать даты не из целевого месяца
        if (prevMonth >= currentMonthStart) {
            setDisplayMonth(prevMonth);
            // Сбрасываем выбранную дату, если она не принадлежит новому отображаемому месяцу
            if (selectedDate && !isSameMonth(selectedDate, prevMonth)) {
                setSelectedDate(null);
                setSelectedWeekday(null);
                setSelectedTimeSlot(null);
                setSelectedStime(null);
            }
        }
    };

    const handleNextMonth = () => {
        const maxMonth = addMonths(targetMonth, 2); // Можно смотреть на 2 месяца вперед от целевого
        if (displayMonth < maxMonth) {
            setDisplayMonth(addMonths(displayMonth, 1));
            // Сбрасываем выбранную дату, если она не принадлежит новому отображаемому месяцу
            if (selectedDate && !isSameMonth(selectedDate, addMonths(displayMonth, 1))) {
                setSelectedDate(null);
                setSelectedWeekday(null);
                setSelectedTimeSlot(null);
                setSelectedStime(null);
            }
        }
    };

    // Проверка, является ли день доступным для выбора
    const isDaySelectable = (day) => {
        // Выбирать можно только дни из целевого месяца (следующего за текущим)
        return isSameMonth(day, targetMonth) && !isPast(day) && isDateAvailable(day);
    };

    useEffect(() => {
        return () => {
            if (bookingTimerRef.current) {
                clearTimeout(bookingTimerRef.current);
            }
            if (slotsLoadingTimerRef.current) {
                clearTimeout(slotsLoadingTimerRef.current);
            }
        };
    }, []);

    const availableSlots = selectedWeekday !== null
        ? getAvailableSlotsForWeekday(selectedWeekday)
        : {};

    if (loading) {
        return (
            <Container fluid className="pool-container">
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3">Загрузка расписания...</p>
                </div>
            </Container>
        );
    }

    if (error) {
        return (
            <Container fluid className="pool-container">
                <Alert variant="danger" className="text-center">
                    <Alert.Heading>Ошибка загрузки</Alert.Heading>
                    <p>{error}</p>
                </Alert>
            </Container>
        );
    }

    const renderMobileCalendar = () => {
        const days = getCalendarDays();

        return (
            <Card className="mb-3 shadow-sm">
                <Card.Header className="d-flex justify-content-between align-items-center py-2">
                    <BootstrapButton
                        variant="outline-primary"
                        onClick={handlePrevMonth}
                        disabled={isSameMonth(displayMonth, new Date())}
                        size="sm"
                    >
                        ←
                    </BootstrapButton>
                    <div className="text-center">
                        <h6 className="mb-0">
                            {format(displayMonth, 'LLLL', { locale: ru })}
                        </h6>
                        <small className="text-muted">{format(displayMonth, 'yyyy')}</small>
                    </div>
                    <BootstrapButton
                        variant="outline-primary"
                        onClick={handleNextMonth}
                        size="sm"
                    >
                        →
                    </BootstrapButton>
                </Card.Header>
                <Card.Body className="p-2">
                    <div className="mobile-calendar-grid">
                        {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map(day => (
                            <div key={day} className="mobile-calendar-header">
                                <small>{day}</small>
                            </div>
                        ))}
                        {days.map(day => {
                            const isSelectable = isDaySelectable(day);
                            const isSelected = selectedDate && isSameDay(day, selectedDate);
                            const isCurrentDay = isToday(day);
                            const isOtherMonth = !isSameMonth(day, displayMonth);
                            const isSameWeekday = selectedWeekday !== null && getDay(day) === selectedWeekday && isSameMonth(day, displayMonth);

                            return (
                                <motion.div
                                    key={day.toString()}
                                    whileTap={isSelectable ? { scale: 0.95 } : {}}
                                    className={`mobile-calendar-day 
                                        ${isSelectable ? 'selectable' : 'disabled'} 
                                        ${isSelected ? 'selected' : ''}
                                        ${isCurrentDay ? 'today' : ''}
                                        ${isOtherMonth ? 'other-month' : ''}
                                        ${isSameWeekday && !isSelected ? 'same-weekday' : ''}`}
                                    onClick={() => isSelectable && handleDateSelect(day)}
                                >
                                    <div className={`mobile-day-number ${isOtherMonth ? 'other-month' : ''}`}>
                                        {format(day, 'd')}
                                    </div>
                                    {isSelected && <div className="mobile-selected-dot"></div>}
                                    {isSameWeekday && !isSelected && (
                                        <div className="mobile-weekday-indicator"></div>
                                    )}
                                </motion.div>
                            );
                        })}
                    </div>

                    <div className="d-flex justify-content-center gap-3 mt-2">
                        <div className="d-flex align-items-center">
                            <div className="legend-dot selected"></div>
                            <small className="ms-1">Выбранный день</small>
                        </div>
                        <div className="d-flex align-items-center">
                            <div className="legend-dot same-weekday"></div>
                            <small className="ms-1">Дни с таким же днем</small>
                        </div>
                    </div>
                </Card.Body>
            </Card>
        );
    };

    const renderDesktopCalendar = () => {
        const days = getCalendarDays();

        return (
            <Card className="shadow-sm desktop-calendar-card">
                <Card.Header className="d-flex justify-content-between align-items-center py-3">
                    <BootstrapButton
                        variant="outline-primary"
                        onClick={handlePrevMonth}
                        disabled={isSameMonth(displayMonth, new Date())}
                        size="sm"
                    >
                        ←
                    </BootstrapButton>
                    <h5 className="mb-0">
                        {format(displayMonth, 'LLLL yyyy', { locale: ru })}
                    </h5>
                    <BootstrapButton
                        variant="outline-primary"
                        onClick={handleNextMonth}
                        size="sm"
                    >
                        →
                    </BootstrapButton>
                </Card.Header>
                <Card.Body className="p-3">
                    <div className="desktop-calendar-grid">
                        {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map(day => (
                            <div key={day} className="desktop-calendar-header">
                                <small>{day}</small>
                            </div>
                        ))}
                        {days.map(day => {
                            const isSelectable = isDaySelectable(day);
                            const isSelected = selectedDate && isSameDay(day, selectedDate);
                            const isCurrentDay = isToday(day);
                            const isOtherMonth = !isSameMonth(day, displayMonth);
                            const isSameWeekday = selectedWeekday !== null && getDay(day) === selectedWeekday && isSameMonth(day, displayMonth);

                            return (
                                <motion.div
                                    key={day.toString()}
                                    whileHover={isSelectable ? { scale: 1.05 } : {}}
                                    whileTap={isSelectable ? { scale: 0.95 } : {}}
                                    className={`desktop-calendar-day 
                                        ${isSelectable ? 'selectable' : 'disabled'} 
                                        ${isSelected ? 'selected' : ''}
                                        ${isCurrentDay ? 'today' : ''}
                                        ${isOtherMonth ? 'other-month' : ''}
                                        ${isSameWeekday && !isSelected ? 'same-weekday' : ''}`}
                                    onClick={() => isSelectable && handleDateSelect(day)}
                                >
                                    <div className={`desktop-day-number ${isOtherMonth ? 'other-month' : ''}`}>
                                        {format(day, 'd')}
                                    </div>
                                    {isSelected && <div className="desktop-selected-dot"></div>}
                                </motion.div>
                            );
                        })}
                    </div>

                    <div className="d-flex justify-content-center gap-4 mt-3">
                        <div className="d-flex align-items-center">
                            <div className="legend-dot selected"></div>
                            <small className="ms-1">Выбранный день</small>
                        </div>
                        <div className="d-flex align-items-center">
                            <div className="legend-dot same-weekday"></div>
                            <small className="ms-1">Дни с таким же днем недели</small>
                        </div>
                        <div className="d-flex align-items-center">
                            <div className="legend-dot today"></div>
                            <small className="ms-1">Сегодня</small>
                        </div>
                    </div>
                </Card.Body>
            </Card>
        );
    };

    const renderMobileTimeSlots = () => (
        <div
            ref={slotsContainerRef}
            style={{
                minHeight: containerHeight,
                transition: 'min-height 0.3s ease'
            }}
        >
            <Card className="shadow-sm mobile-time-slots-card">
                <Card.Header className="py-2">
                    <h6 className="mb-0 text-center">
                        {selectedDate ? (
                            <>
                                {format(selectedDate, 'dd.MM.yyyy')} -
                                {selectedWeekday !== null && (
                                    <span className="text-primary ms-1">
                                        {getWeekdayName(selectedWeekday)}
                                    </span>
                                )}
                            </>
                        ) : 'Выберите дату'}
                    </h6>
                </Card.Header>
                <Card.Body className="p-2">
                    <AnimatePresence mode="wait">
                        {isTimeSlotsVisible && selectedDate ? (
                            <motion.div
                                key={`slots-${selectedDate.toISOString()}`}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                transition={{ duration: 0.3 }}
                            >
                                <div className="mobile-time-slots-grid">
                                    {Object.entries(TIME_SLOTS_MAP).map(([stime, timeSlot]) => {
                                        const slotData = availableSlots[timeSlot];
                                        if (!slotData) return null;

                                        return (
                                            <TimeSlotCard
                                                key={stime}
                                                slot={timeSlot}
                                                stime={stime}
                                                availableCount={slotData.available}
                                                totalCount={slotData.total}
                                                isSelected={selectedTimeSlot === timeSlot}
                                                isMobileView={true}
                                                onSelect={handleSelectSlot}
                                                isBooking={bookingState.isBooking && bookingState.bookingSlot === timeSlot}
                                                bookedSlots={bookedSlots}
                                                selectedWeekday={selectedWeekday}
                                                isAvailable={slotData.available > 0}
                                            />
                                        );
                                    })}
                                </div>
                            </motion.div>
                        ) : selectedDate ? (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="text-center py-4"
                            >
                                <Spinner animation="border" variant="primary" size="sm" />
                                <p className="mt-2 small">Загрузка доступного времени...</p>
                            </motion.div>
                        ) : (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="text-center py-4"
                            >
                                <p className="text-muted small mb-0">
                                    Выберите дату для просмотра доступного времени
                                </p>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </Card.Body>
            </Card>
        </div>
    );

    const renderDesktopTimeSlots = () => (
        <Card className="shadow-sm desktop-time-slots-card">
            <Card.Header className="py-3">
                <h5 className="mb-0 text-center">
                    {selectedDate ? (
                        <>
                            {format(selectedDate, 'dd.MM.yyyy')}
                            {selectedWeekday !== null && (
                                <span className="text-primary ms-2">
                                    ({getWeekdayName(selectedWeekday)})
                                </span>
                            )}
                        </>
                    ) : 'Выберите дату'}
                </h5>
            </Card.Header>
            <Card.Body className="p-3">
                <AnimatePresence mode="wait">
                    {isTimeSlotsVisible && selectedDate ? (
                        <motion.div
                            key={`slots-${selectedDate.toISOString()}`}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.3 }}
                        >
                            <div className="desktop-time-slots-grid">
                                {Object.entries(TIME_SLOTS_MAP).map(([stime, timeSlot]) => {
                                    const slotData = availableSlots[timeSlot];
                                    if (!slotData) return null;

                                    return (
                                        <TimeSlotCard
                                            key={stime}
                                            slot={timeSlot}
                                            stime={stime}
                                            availableCount={slotData.available}
                                            totalCount={slotData.total}
                                            isSelected={selectedTimeSlot === timeSlot}
                                            isMobileView={false}
                                            onSelect={handleSelectSlot}
                                            isBooking={bookingState.isBooking && bookingState.bookingSlot === timeSlot}
                                            bookedSlots={bookedSlots}
                                            selectedWeekday={selectedWeekday}
                                            isAvailable={slotData.available > 0}
                                        />
                                    );
                                })}
                            </div>
                        </motion.div>
                    ) : selectedDate ? (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="text-center py-5"
                        >
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-2">Загрузка доступного времени...</p>
                        </motion.div>
                    ) : (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="text-center py-5"
                        >
                            <p className="text-muted mb-0">
                                Выберите дату для просмотра доступных слотов
                            </p>
                        </motion.div>
                    )}
                </AnimatePresence>
            </Card.Body>
        </Card>
    );

    return (
        <Container fluid className='pool-container'>
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
            >
                {!isMobile && (
                    <Card className="mb-3 shadow-sm">
                        <Card.Header className="bg-primary text-white py-2">
                            <h4 className="mb-0">Оформление абонемента в бассейн</h4>
                        </Card.Header>
                        <Card.Body className="py-2">
                            <p className="text-muted mb-0 small">
                                Выберите день недели и время. Абонемент будет действовать на {format(targetMonth, 'LLLL yyyy', { locale: ru })}.
                            </p>
                        </Card.Body>
                    </Card>
                )}

                {scheduleData && (
                    <Card className={`mb-3 shadow-sm ${isBookingAvailable ? 'bg-success' : 'bg-warning'} text-white`}>
                        <Card.Body className="py-2">
                            <p className="mb-0 small">
                                <strong>Период бронирования:</strong> {scheduleData.BOOKB} - {scheduleData.BOOKE}
                                {!isBookingAvailable && (
                                    <span className="ms-2">(Бронирование временно недоступно)</span>
                                )}
                            </p>
                        </Card.Body>
                    </Card>
                )}

                {isMobile ? (
                    <div className="mobile-container">
                        <Card className="mb-3 shadow-sm">
                            <Card.Header className="bg-primary text-white py-2">
                                <h5 className="mb-0">Абонемент в бассейн на {format(targetMonth, 'LLLL', { locale: ru })}</h5>
                            </Card.Header>
                            <Card.Body className="py-2">
                                <p className="text-muted mb-0 small">
                                    Выберите день недели и время
                                </p>
                            </Card.Body>
                        </Card>

                        {renderMobileCalendar()}

                        {selectedDate && renderMobileTimeSlots()}
                    </div>
                ) : (
                    <Row className="desktop-layout g-4">
                        <Col lg={6} xl={6} className="desktop-calendar-column">
                            {renderDesktopCalendar()}
                        </Col>
                        <Col lg={6} xl={6} className="desktop-timeslots-column">
                            {renderDesktopTimeSlots()}
                        </Col>
                    </Row>
                )}
            </motion.div>

            <BookingConfirmationModal
                show={showModal}
                onClose={() => setShowModal(false)}
                onConfirm={handleConfirmBooking}
                bookingDetails={bookingDetails}
            />
        </Container>
    );
}

export default PoolBooking;